import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Plus, X, RotateCcw, ChevronRight, Trash2, Check, Truck, FileCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { inr, fmtQty } from "./lib";

const STATUS_META = {
  draft:            { label: "Draft",           chip: "bg-[#98A2B3]/20 text-[#475467]" },
  dispatched:       { label: "Dispatched",      chip: "bg-[#F79009]/15 text-[#B54708]" },
  credit_received:  { label: "Credit Received", chip: "bg-[#12B76A]/12 text-[#0F9256]" },
};

export default function PurchaseReturns() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/spares/purchase-returns", { params: { status: status || undefined } })
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load();   }, [status]);

  return (
    <div className="space-y-4" data-testid="purchase-returns-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-[22px] md:text-[26px] font-bold text-[#1d3557] leading-tight">Purchase Returns</h1>
          <p className="text-[12.5px] text-[#667085] mt-0.5">
            Return defective / excess parts to your vendors with credit tracking.
          </p>
        </div>
        <button onClick={() => setCreateOpen(true)} data-testid="pr-new-btn"
                className="h-10 px-4 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5">
          <Plus size={13} /> New Return
        </button>
      </header>

      <div className="flex flex-wrap gap-1 bg-white rounded-lg border border-[#E4E7EC] p-1 w-fit">
        {[
          { key: "", label: "All" },
          { key: "draft", label: "Draft" },
          { key: "dispatched", label: "Dispatched" },
          { key: "credit_received", label: "Credit Received" },
        ].map((v) => (
          <button key={v.key || "all"} onClick={() => setStatus(v.key)}
                  data-testid={`pr-view-${v.key || "all"}`}
                  className={`h-8 px-3 rounded-md text-[11.5px] font-semibold ${
                    status === v.key ? "bg-[#1d3557] text-white" : "text-[#667085] hover:text-[#1d3557]"
                  }`}>{v.label}</button>
        ))}
      </div>

      <section className="bg-white rounded-xl border border-[#E4E7EC]">
        {loading ? (
          <div className="p-10 text-center text-[13px] text-[#667085]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-14 text-center" data-testid="pr-empty">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#F1FAEE] grid place-items-center text-[#457b9d] mb-3">
              <RotateCcw size={20} />
            </div>
            <div className="text-[14px] font-semibold text-[#1d3557]">No returns yet</div>
            <p className="text-[12px] text-[#667085] mt-1">Create a return the next time you send parts back.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="pr-table">
              <thead>
                <tr className="text-left text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] border-b border-[#F1FAEE]">
                  <th className="px-5 py-2.5">Number</th>
                  <th className="px-3 py-2.5">Vendor</th>
                  <th className="px-3 py-2.5 text-right">Lines</th>
                  <th className="px-3 py-2.5 text-right">Amount</th>
                  <th className="px-3 py-2.5">Status</th>
                  <th className="px-3 py-2.5">Date</th>
                  <th className="px-5 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const meta = STATUS_META[r.status] || STATUS_META.draft;
                  return (
                    <tr key={r.id} data-testid={`pr-row-${r.id}`}
                        className="border-b border-[#F1FAEE] hover:bg-[#F7F9FC] cursor-pointer"
                        onClick={() => nav(`/spare-parts/returns/${r.id}`)}>
                      <td className="px-5 py-3 font-mono font-semibold text-[#1d3557]">{r.number}</td>
                      <td className="px-3 py-3 text-[#1d3557]">{r.vendor_snapshot?.name || "—"}</td>
                      <td className="px-3 py-3 text-right font-mono text-[#667085]">{(r.lines || []).length}</td>
                      <td className="px-3 py-3 text-right font-mono font-bold">{inr(r.totals?.grand_total)}</td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider ${meta.chip}`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-[#667085] text-[11.5px]">{new Date(r.created_at).toLocaleDateString()}</td>
                      <td className="px-5 py-3 text-right text-[#a8dadc]"><ChevronRight size={14} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {createOpen && (
        <NewReturnDialog onClose={() => setCreateOpen(false)}
                         onSaved={(r) => { setCreateOpen(false); load(); nav(`/spare-parts/returns/${r.id}`); }} />
      )}
    </div>
  );
}

function NewReturnDialog({ onClose, onSaved }) {
  const [vendors, setVendors] = useState([]);
  const [parts, setParts] = useState([]);
  const [vendorId, setVendorId] = useState("");
  const [lines, setLines] = useState([{ part_id: null, name: "", part_number: "", qty: 1, rate: 0, gst_rate: 18, reason: "" }]);
  const [isInterstate, setIsInterstate] = useState(false);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/spares/vendors", { params: { active_only: true } }).then((r) => setVendors(r.data || []));
    api.get("/spares/parts", { params: { limit: 1000 } }).then((r) => setParts(r.data || []));
  }, []);

  const setLine = (idx, patch) => setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, { part_id: null, name: "", part_number: "", qty: 1, rate: 0, gst_rate: 18, reason: "" }]);
  const removeLine = (idx) => setLines((ls) => (ls.length > 1 ? ls.filter((_, i) => i !== idx) : ls));

  const pickPart = (idx, p) => setLine(idx, {
    part_id: p.id, name: p.name, part_number: p.part_number || "",
    rate: p.purchase_price || 0, gst_rate: p.gst_rate ?? 18, max_available: p.available_qty,
  });

  const canSave = vendorId && lines.some((l) => l.part_id && Number(l.qty) > 0);

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      const payload = {
        vendor_id: vendorId,
        lines: lines.filter((l) => l.part_id && Number(l.qty) > 0).map((l) => ({
          part_id: l.part_id, name: l.name, part_number: l.part_number,
          qty: Number(l.qty), rate: Number(l.rate) || 0, gst_rate: Number(l.gst_rate) || 0,
          reason: l.reason || "",
        })),
        is_interstate: isInterstate, reason, notes,
      };
      const r = await api.post("/spares/purchase-returns", payload);
      toast.success(`Return ${r.data.number} created`);
      onSaved(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not create return");
    } finally { setSaving(false); }
  };

  const totals = useMemo(() => {
    let taxable = 0, gst = 0;
    for (const ln of lines) {
      const t = (Number(ln.qty) || 0) * (Number(ln.rate) || 0);
      taxable += t; gst += t * (Number(ln.gst_rate) || 0) / 100;
    }
    return { taxable, gst, grand: taxable + gst };
  }, [lines]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="pr-dialog-backdrop">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[94vh]"
           data-testid="pr-dialog">
        <header className="px-6 py-4 border-b border-[#E4E7EC] flex items-center justify-between bg-[#F7F9FC]">
          <div>
            <h2 className="text-[16px] font-bold text-[#1d3557]">New Purchase Return</h2>
            <p className="text-[11.5px] text-[#667085]">Return parts to a vendor. Stock deducts on dispatch.</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-md hover:bg-[#E4E7EC] grid place-items-center text-[#667085]"
                  data-testid="pr-dialog-close">
            <X size={17} />
          </button>
        </header>
        <div className="p-5 overflow-y-auto flex-1 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Vendor *</span>
              <select value={vendorId} onChange={(e) => setVendorId(e.target.value)}
                      data-testid="pr-vendor"
                      className="mt-1 h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white">
                <option value="">Pick vendor…</option>
                {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Overall reason</span>
              <input value={reason} onChange={(e) => setReason(e.target.value)}
                     placeholder="Damaged batch / Excess / Wrong part"
                     data-testid="pr-reason"
                     className="mt-1 h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Lines</div>
              <button type="button" onClick={addLine} data-testid="pr-add-line"
                      className="h-7 px-2 rounded-md border border-[#E4E7EC] text-[11.5px] font-semibold text-[#457b9d] hover:bg-[#F1FAEE] inline-flex items-center gap-1">
                <Plus size={11} /> Add
              </button>
            </div>
            <div className="space-y-2">
              {lines.map((ln, idx) => (
                <PrLineRow key={idx} idx={idx} ln={ln} parts={parts}
                           onChange={(patch) => setLine(idx, patch)}
                           onPart={(p) => pickPart(idx, p)}
                           onRemove={() => removeLine(idx)}
                           removable={lines.length > 1} />
              ))}
            </div>
          </div>

          <label className="inline-flex items-center gap-2 text-[12px] text-[#1d3557]">
            <input type="checkbox" checked={isInterstate} onChange={(e) => setIsInterstate(e.target.checked)}
                   className="w-4 h-4 accent-[#e63946]" />
            Inter-state (IGST)
          </label>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                    placeholder="Internal notes"
                    className="w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px]" />

          <div className="rounded-lg bg-[#F1FAEE] border border-[#a8dadc]/40 p-3 text-[12.5px] text-[#1d3557]" data-testid="pr-totals">
            Total return value: <span className="font-bold font-mono">{inr(totals.grand)}</span>
            <span className="text-[#457b9d] ml-2 text-[11px]">(Taxable {inr(totals.taxable)} · GST {inr(totals.gst)})</span>
          </div>

          {error && (
            <div className="rounded-lg border border-[#e63946]/30 bg-[#e63946]/10 px-3 py-2 text-[12px] text-[#e63946]"
                 data-testid="pr-error">
              {error}
            </div>
          )}
        </div>
        <footer className="px-6 py-3.5 border-t border-[#E4E7EC] bg-[#F7F9FC] flex justify-end gap-2">
          <button onClick={onClose}
                  className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#667085] hover:bg-white">
            Cancel
          </button>
          <button onClick={submit} disabled={!canSave || saving}
                  data-testid="pr-submit-btn"
                  className="h-10 px-5 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
            <Check size={13} /> {saving ? "Creating…" : "Create return"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function PrLineRow({ idx, ln, parts, onChange, onPart, onRemove, removable }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const results = q
    ? parts.filter((p) => (p.name || "").toLowerCase().includes(q.toLowerCase()) ||
                          (p.part_number || "").toLowerCase().includes(q.toLowerCase())).slice(0, 8)
    : parts.slice(0, 8);
  return (
    <div className="rounded-lg border border-[#E4E7EC] p-3" data-testid={`pr-line-${idx}`}>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
        <div className="md:col-span-5 relative">
          <input value={ln.part_id ? ln.name : q}
                 onFocus={() => setOpen(true)}
                 onBlur={() => setTimeout(() => setOpen(false), 200)}
                 onChange={(e) => { setQ(e.target.value); onChange({ part_id: null, name: e.target.value }); }}
                 placeholder="Search part…"
                 data-testid={`pr-line-${idx}-part`}
                 className="w-full h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px]" />
          {ln.part_id && (
            <div className="text-[10.5px] font-mono text-[#0F9256] mt-0.5">
              avail {ln.max_available ?? "—"}
            </div>
          )}
          {open && results.length > 0 && (
            <ul className="absolute z-10 mt-1 bg-white border border-[#E4E7EC] rounded-md shadow-lg w-full max-h-52 overflow-y-auto text-[12.5px]">
              {results.map((p) => (
                <li key={p.id}>
                  <button type="button"
                          onMouseDown={(e) => { e.preventDefault(); onPart(p); setOpen(false); setQ(p.name); }}
                          className="w-full text-left px-3 py-1.5 hover:bg-[#F1FAEE]">
                    <div className="font-semibold text-[#1d3557]">{p.name}</div>
                    <div className="text-[10.5px] text-[#667085]">
                      {p.part_number || ""} · avail {p.available_qty}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <input type="number" min={0} value={ln.qty}
               onChange={(e) => onChange({ qty: Number(e.target.value) })}
               placeholder="Qty" data-testid={`pr-line-${idx}-qty`}
               className="md:col-span-1 h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px] font-mono" />
        <input type="number" min={0} value={ln.rate}
               onChange={(e) => onChange({ rate: Number(e.target.value) })}
               placeholder="Rate"
               className="md:col-span-2 h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px] font-mono" />
        <input type="number" min={0} value={ln.gst_rate}
               onChange={(e) => onChange({ gst_rate: Number(e.target.value) })}
               placeholder="GST%"
               className="md:col-span-1 h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px] font-mono" />
        <input value={ln.reason} onChange={(e) => onChange({ reason: e.target.value })}
               placeholder="Line reason"
               className="md:col-span-2 h-9 px-2 rounded-md border border-[#E4E7EC] text-[13px]" />
        <button type="button" onClick={onRemove} disabled={!removable}
                data-testid={`pr-line-${idx}-remove`}
                className="md:col-span-1 w-full h-9 rounded-md border border-[#E4E7EC] grid place-items-center text-[#a8dadc] hover:text-[#e63946] hover:border-[#e63946]/40 disabled:opacity-30">
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

// -------- Detail --------
export function PurchaseReturnDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creditOpen, setCreditOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/spares/purchase-returns/${id}`)
      .then((res) => setR(res.data))
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Could not load return");
        nav("/spare-parts/returns");
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load();   }, [id]);

  const dispatch = async () => {
    if (!window.confirm("Dispatch this return? Stock will be deducted immediately.")) return;
    try {
      await api.post(`/spares/purchase-returns/${id}/dispatch`);
      toast.success("Return dispatched · stock updated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not dispatch"); }
  };

  if (loading || !r) return <div className="py-10 text-center text-[13px] text-[#667085]">Loading…</div>;
  const meta = STATUS_META[r.status] || STATUS_META.draft;

  return (
    <div className="space-y-4" data-testid="pr-detail-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <Link to="/spare-parts/returns" data-testid="pr-detail-back"
                className="h-9 w-9 rounded-md border border-[#E4E7EC] grid place-items-center text-[#667085] hover:bg-[#F1FAEE]">
            <ArrowLeft size={15} />
          </Link>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-[22px] md:text-[26px] font-bold text-[#1d3557] leading-tight font-mono">{r.number}</h1>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider ${meta.chip}`}
                    data-testid="pr-detail-status">{meta.label}</span>
            </div>
            <div className="text-[12.5px] text-[#667085] mt-0.5">
              {r.vendor_snapshot?.name} · {new Date(r.created_at).toLocaleString()}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {r.status === "draft" && (
            <button onClick={dispatch} data-testid="pr-detail-dispatch"
                    className="h-10 px-4 rounded-md bg-[#F79009] hover:bg-[#B54708] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5">
              <Truck size={13} /> Dispatch
            </button>
          )}
          {r.status === "dispatched" && (
            <button onClick={() => setCreditOpen(true)} data-testid="pr-detail-credit"
                    className="h-10 px-4 rounded-md bg-[#12B76A] hover:bg-[#0F9256] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5">
              <FileCheck size={13} /> Mark credit received
            </button>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <section className="lg:col-span-2 bg-white rounded-xl border border-[#E4E7EC] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#F1FAEE]">
            <h3 className="text-[13px] font-bold text-[#1d3557]">Line items</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="pr-lines-table">
              <thead>
                <tr className="text-left text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] border-b border-[#F1FAEE]">
                  <th className="px-4 py-2.5">Part</th>
                  <th className="px-3 py-2.5 text-right">Qty</th>
                  <th className="px-3 py-2.5 text-right">Rate</th>
                  <th className="px-3 py-2.5 text-right">GST</th>
                  <th className="px-3 py-2.5 text-right">Total</th>
                  <th className="px-3 py-2.5">Reason</th>
                </tr>
              </thead>
              <tbody>
                {(r.lines || []).map((ln) => (
                  <tr key={ln.id} className="border-b border-[#F1FAEE] last:border-0">
                    <td className="px-4 py-2.5">
                      <div className="font-semibold text-[#1d3557]">{ln.name}</div>
                      {ln.part_number && <div className="text-[11px] font-mono text-[#667085]">{ln.part_number}</div>}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">{ln.qty}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{inr(ln.rate)}</td>
                    <td className="px-3 py-2.5 text-right text-[#667085]">{ln.gst_rate || 0}%</td>
                    <td className="px-3 py-2.5 text-right font-mono font-bold">{inr(ln.total)}</td>
                    <td className="px-3 py-2.5 text-[#667085] text-[11.5px]">{ln.reason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <aside>
          <section className="bg-white rounded-xl border border-[#E4E7EC] p-4 sticky top-4">
            <h3 className="text-[13px] font-bold text-[#1d3557] mb-3">Summary</h3>
            <div className="space-y-1 text-[12.5px]">
              <Row label="Taxable" value={inr(r.totals?.taxable)} muted />
              {r.is_interstate
                ? <Row label="IGST" value={inr(r.totals?.igst)} muted />
                : (<>
                    <Row label="CGST" value={inr(r.totals?.cgst)} muted />
                    <Row label="SGST" value={inr(r.totals?.sgst)} muted />
                  </>)}
              <div className="my-2 border-t border-[#F1FAEE]" />
              <Row label="Return value" value={inr(r.totals?.grand_total)} big />
              {r.status === "credit_received" && (
                <>
                  <div className="my-2 border-t border-[#F1FAEE]" />
                  <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#0F9256]">Credit received</div>
                  <Row label="Credit note" value={r.credit_note_number || "—"} muted />
                  <Row label="Credit amount" value={inr(r.credit_amount)} />
                </>
              )}
            </div>
          </section>
        </aside>
      </div>

      {creditOpen && (
        <CreditDialog rid={r.id} expected={r.totals?.grand_total}
                      onClose={() => setCreditOpen(false)}
                      onSaved={() => { setCreditOpen(false); load(); }} />
      )}
    </div>
  );
}

function CreditDialog({ rid, expected, onClose, onSaved }) {
  const [creditNote, setCreditNote] = useState("");
  const [amount, setAmount] = useState(expected || 0);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      await api.post(`/spares/purchase-returns/${rid}/credit-received`, {
        credit_note_number: creditNote || "", credit_amount: Number(amount) || 0, notes,
      });
      toast.success("Credit recorded");
      onSaved();
    } catch (e) { setError(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="credit-dialog-backdrop">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
           data-testid="credit-dialog">
        <header className="px-6 py-4 border-b border-[#E4E7EC] bg-[#F7F9FC]">
          <h2 className="text-[16px] font-bold text-[#1d3557]">Mark credit received</h2>
        </header>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Credit note #</span>
            <input value={creditNote} onChange={(e) => setCreditNote(e.target.value)}
                   data-testid="credit-note-number"
                   className="mt-1 h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
          </label>
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Credit amount *</span>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                   data-testid="credit-amount"
                   className="mt-1 h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
          </label>
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Notes</span>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                      className="mt-1 w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px]" />
          </label>
          {error && (
            <div className="rounded-lg border border-[#e63946]/30 bg-[#e63946]/10 px-3 py-2 text-[12px] text-[#e63946]">{error}</div>
          )}
        </div>
        <footer className="px-6 py-3.5 border-t border-[#E4E7EC] bg-[#F7F9FC] flex justify-end gap-2">
          <button onClick={onClose}
                  className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#667085] hover:bg-white">
            Cancel
          </button>
          <button onClick={save} disabled={saving}
                  data-testid="credit-save-btn"
                  className="h-10 px-5 rounded-md bg-[#12B76A] hover:bg-[#0F9256] text-white text-[12.5px] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
            <Check size={13} /> {saving ? "Saving…" : "Save"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function Row({ label, value, muted, big }) {
  return (
    <div className={`flex items-center justify-between py-1 ${big ? "text-[15px] font-bold" : "text-[12.5px]"}`}>
      <span className={muted ? "text-[#667085]" : "text-[#1d3557]"}>{label}</span>
      <span className={`font-mono tabular-nums ${muted ? "text-[#667085]" : "text-[#1d3557]"}`}>{value}</span>
    </div>
  );
}
